import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { graphTrpc } from '@org/clients/graph';
import { communityTrpc } from '@org/clients/community';
import {
  useToast,
  Button,
  Input,
  Textarea,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Switch,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  MediaUploader,
} from '@org/ui';
import {
  User,
  Shield,
  Bell,
  UserX,
  Save,
  Loader2,
  Instagram,
  Linkedin,
  Globe,
  MapPin,
  Languages,
  Target,
  Camera,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { formatDistanceToNow } from 'date-fns';

const INTEREST_OPTIONS = [
  'reading', 'cooking', 'travel', 'cinema', 'music',
  'sports', 'tech', 'art', 'wellness', 'languages',
] as const;

export const Route = createFileRoute('/settings')({
  component: SettingsPage,
});

function SettingsPage() {
  const { t } = useTranslation('community');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">{t('settings.title')}</h1>
        <p className="text-muted-foreground">{t('settings.subtitle')}</p>
      </div>

      <Tabs defaultValue="profile">
        <TabsList className="w-full justify-start">
          <TabsTrigger value="profile" className="flex items-center gap-2">
            <User className="h-4 w-4" aria-hidden="true" />
            {t('settings.profileTab')}
          </TabsTrigger>
          <TabsTrigger value="notifications" className="flex items-center gap-2">
            <Bell className="h-4 w-4" aria-hidden="true" />
            {t('settings.notifications')}
          </TabsTrigger>
          <TabsTrigger value="privacy" className="flex items-center gap-2">
            <Shield className="h-4 w-4" aria-hidden="true" />
            {t('settings.privacy')}
          </TabsTrigger>
          <TabsTrigger value="blocked" className="flex items-center gap-2">
            <UserX className="h-4 w-4" aria-hidden="true" />
            {t('settings.blockedTab')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-6">
          <ProfileSettings />
        </TabsContent>

        <TabsContent value="notifications" className="mt-6">
          <NotificationSettings />
        </TabsContent>

        <TabsContent value="privacy" className="mt-6">
          <PrivacySettings />
        </TabsContent>

        <TabsContent value="blocked" className="mt-6">
          <BlockedUsersSettings />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ProfileSettings() {
  const { t } = useTranslation('community');
  const { toast } = useToast();
  const { data: profile, isLoading } = graphTrpc.profiles.getMyProfile.useQuery();
  const utils = graphTrpc.useUtils();

  const [formData, setFormData] = useState({
    displayName: '',
    bio: '',
    location: '',
    languagesSpoken: [] as string[],
    interests: [] as string[],
    learningGoals: '',
    socialMediaLinks: {
      instagram: '',
      linkedin: '',
      website: '',
    },
  });

  const [languageInput, setLanguageInput] = useState('');
  const [showAvatarUpload, setShowAvatarUpload] = useState(false);

  useEffect(() => {
    if (profile) {
      setFormData({
        displayName: profile.displayName || '',
        bio: profile.bio || '',
        location: profile.location || '',
        languagesSpoken: profile.languagesSpoken || [],
        interests: profile.interests || [],
        learningGoals: profile.learningGoals || '',
        socialMediaLinks: {
          instagram: (profile.socialMediaLinks as any)?.instagram || '',
          linkedin: (profile.socialMediaLinks as any)?.linkedin || '',
          website: (profile.socialMediaLinks as any)?.website || '',
        },
      });
    }
  }, [profile]);

  const updateProfile = graphTrpc.profiles.updateProfile.useMutation({
    onSuccess: () => {
      utils.profiles.getMyProfile.invalidate();
      toast({ title: t('settings.saved'), description: t('settings.savedDescription') });
    },
    onError: (error) => {
      toast({ title: t('common.error'), description: error.message, variant: 'destructive' });
    },
  });

  const handleAvatarUpload = (url: string) => {
    setShowAvatarUpload(false);
    updateProfile.mutate({ profileImageUrl: url });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateProfile.mutate({
      displayName: formData.displayName || undefined,
      bio: formData.bio || undefined,
      location: formData.location || undefined,
      languagesSpoken: formData.languagesSpoken.length > 0 ? formData.languagesSpoken : undefined,
      interests: formData.interests.length > 0 ? formData.interests : undefined,
      learningGoals: formData.learningGoals || undefined,
      socialMediaLinks: {
        instagram: formData.socialMediaLinks.instagram || undefined,
        linkedin: formData.socialMediaLinks.linkedin || undefined,
        website: formData.socialMediaLinks.website || undefined,
      },
    });
  };

  const addLanguage = () => {
    if (languageInput.trim() && !formData.languagesSpoken.includes(languageInput.trim())) {
      setFormData({
        ...formData,
        languagesSpoken: [...formData.languagesSpoken, languageInput.trim()],
      });
      setLanguageInput('');
    }
  };

  const removeLanguage = (lang: string) => {
    setFormData({
      ...formData,
      languagesSpoken: formData.languagesSpoken.filter((l) => l !== lang),
    });
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6 space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    );
  }

  const displayName = profile?.displayName ||
    [profile?.firstName, profile?.lastName].filter(Boolean).join(' ') || '';
  const initial = displayName.slice(0, 1).toUpperCase() || '?';

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('settings.profileInfo')}</CardTitle>
        <CardDescription>{t('settings.profileInfoDescription')}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Avatar */}
          <div className="space-y-2">
            <Label>{t('settings.avatar')}</Label>
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => setShowAvatarUpload(!showAvatarUpload)}
                className="group relative"
                aria-label={t('settings.avatarDescription')}
              >
                <Avatar className="h-20 w-20">
                  <AvatarImage src={profile?.profileImageUrl ?? undefined} />
                  <AvatarFallback className="bg-primary/10 text-xl text-primary">
                    {initial}
                  </AvatarFallback>
                </Avatar>
                <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
                  <Camera className="h-6 w-6 text-white" aria-hidden="true" />
                </div>
              </button>
              <p className="text-sm text-muted-foreground">{t('settings.avatarDescription')}</p>
            </div>
            {showAvatarUpload && (
              <div className="mt-2">
                <MediaUploader
                  onUploadComplete={handleAvatarUpload}
                  acceptedFileTypes={['image/jpeg', 'image/png', 'image/webp']}
                  maxFileSize={5 * 1024 * 1024}
                  compact
                />
              </div>
            )}
          </div>

          {/* Display Name */}
          <div className="space-y-2">
            <Label htmlFor="displayName">{t('settings.displayName')}</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
              <Input
                id="displayName"
                value={formData.displayName}
                onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                placeholder={t('settings.displayNamePlaceholder')}
                className="pl-9"
                maxLength={100}
              />
            </div>
            <p className="text-xs text-muted-foreground">{t('settings.displayNameHint')}</p>
          </div>

          {/* Bio */}
          <div className="space-y-2">
            <Label htmlFor="bio">{t('settings.bio')}</Label>
            <Textarea
              id="bio"
              value={formData.bio}
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
              placeholder={t('settings.bioPlaceholder')}
              rows={4}
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground">
              {t('settings.bioCount', { count: formData.bio.length, max: 500 })}
            </p>
          </div>

          {/* Location */}
          <div className="space-y-2">
            <Label htmlFor="location">{t('settings.location')}</Label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder={t('settings.locationPlaceholder')}
                className="pl-9"
                maxLength={100}
              />
            </div>
          </div>

          {/* Languages */}
          <div className="space-y-2">
            <Label>{t('settings.languages')}</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Languages className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                <Input
                  value={languageInput}
                  onChange={(e) => setLanguageInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addLanguage();
                    }
                  }}
                  placeholder={t('settings.languagePlaceholder')}
                  className="pl-9"
                />
              </div>
              <Button type="button" variant="outline" onClick={addLanguage}>
                {t('settings.languageAdd')}
              </Button>
            </div>
            {formData.languagesSpoken.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.languagesSpoken.map((lang) => (
                  <span
                    key={lang}
                    className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-sm"
                  >
                    {lang}
                    <button
                      type="button"
                      onClick={() => removeLanguage(lang)}
                      className="ml-1 text-muted-foreground hover:text-foreground"
                      aria-label={`Remove ${lang}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Interests */}
          <div className="space-y-2">
            <Label>{t('settings.interests')}</Label>
            <p className="text-xs text-muted-foreground">{t('settings.interestsHint')}</p>
            <div className="flex flex-wrap gap-2">
              {INTEREST_OPTIONS.map((interest) => {
                const selected = formData.interests.includes(interest);
                return (
                  <button
                    key={interest}
                    type="button"
                    onClick={() => {
                      setFormData({
                        ...formData,
                        interests: selected
                          ? formData.interests.filter((i) => i !== interest)
                          : formData.interests.length < 10
                            ? [...formData.interests, interest]
                            : formData.interests,
                      });
                    }}
                    className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
                      selected
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border bg-card text-muted-foreground hover:border-primary/50'
                    }`}
                    aria-pressed={selected}
                  >
                    {t(`settings.interest.${interest}`)}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Learning Goals */}
          <div className="space-y-2">
            <Label htmlFor="learningGoals">{t('settings.learningGoals')}</Label>
            <div className="relative">
              <Target className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <Textarea
                id="learningGoals"
                value={formData.learningGoals}
                onChange={(e) => setFormData({ ...formData, learningGoals: e.target.value })}
                placeholder={t('settings.learningGoalsPlaceholder')}
                className="pl-9"
                rows={3}
                maxLength={300}
              />
            </div>
          </div>

          {/* Social Links */}
          <div className="space-y-4">
            <Label>{t('settings.socialLinks')}</Label>

            <div className="space-y-3">
              <div className="relative">
                <Instagram className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                <Input
                  value={formData.socialMediaLinks.instagram}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      socialMediaLinks: { ...formData.socialMediaLinks, instagram: e.target.value },
                    })
                  }
                  placeholder={t('settings.instagramPlaceholder')}
                  className="pl-9"
                />
              </div>

              <div className="relative">
                <Linkedin className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                <Input
                  value={formData.socialMediaLinks.linkedin}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      socialMediaLinks: { ...formData.socialMediaLinks, linkedin: e.target.value },
                    })
                  }
                  placeholder={t('settings.linkedinPlaceholder')}
                  className="pl-9"
                />
              </div>

              <div className="relative">
                <Globe className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                <Input
                  value={formData.socialMediaLinks.website}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      socialMediaLinks: { ...formData.socialMediaLinks, website: e.target.value },
                    })
                  }
                  placeholder={t('settings.websitePlaceholder')}
                  className="pl-9"
                />
              </div>
            </div>
          </div>

          <Button type="submit" disabled={updateProfile.isPending} className="gap-2">
            {updateProfile.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                {t('settings.saving')}
              </>
            ) : (
              <>
                <Save className="h-4 w-4" aria-hidden="true" />
                {t('settings.save')}
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function NotificationSettings() {
  const { t } = useTranslation('community');
  const [preferences, setPreferences] = useState({
    postReplies: true,
    postComments: true,
    postLikes: true,
    mentions: true,
    newFollowers: true,
    levelUp: true,
    badgeEarned: true,
    emailDigest: false,
  });

  const utils = communityTrpc.useUtils();
  const { data: popupPref } = communityTrpc.preferences.getPopupPreferences.useQuery();
  const popupEnabled = popupPref?.popupEnabled ?? true;
  const setPopupPreferences = communityTrpc.preferences.setPopupPreferences.useMutation({
    onMutate: async ({ popupEnabled: next }) => {
      await utils.preferences.getPopupPreferences.cancel();
      const previous = utils.preferences.getPopupPreferences.getData();
      utils.preferences.getPopupPreferences.setData(undefined, { popupEnabled: next });
      return { previous };
    },
    onError: (_err, _input, context) => {
      if (context?.previous) {
        utils.preferences.getPopupPreferences.setData(undefined, context.previous);
      }
    },
    onSettled: () => {
      utils.preferences.getPopupPreferences.invalidate();
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('settings.notificationPrefs')}</CardTitle>
        <CardDescription>{t('settings.notificationPrefsDescription')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <h3 className="text-sm font-medium">{t('settings.notifications')}</h3>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{t('notifications.popupAlerts')}</p>
              <p className="text-sm text-muted-foreground">{t('notifications.popupAlertsHelper')}</p>
            </div>
            <Switch
              checked={popupEnabled}
              onCheckedChange={(checked) => setPopupPreferences.mutate({ popupEnabled: checked })}
            />
          </div>
        </div>

        <div className="space-y-4 border-t pt-6">
          <h3 className="text-sm font-medium">{t('settings.activityNotifications')}</h3>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{t('settings.postReplies')}</p>
              <p className="text-sm text-muted-foreground">{t('settings.postRepliesDescription')}</p>
            </div>
            <Switch
              checked={preferences.postReplies}
              onCheckedChange={(checked) => setPreferences({ ...preferences, postReplies: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{t('settings.comments')}</p>
              <p className="text-sm text-muted-foreground">{t('settings.commentsDescription')}</p>
            </div>
            <Switch
              checked={preferences.postComments}
              onCheckedChange={(checked) => setPreferences({ ...preferences, postComments: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{t('settings.likes')}</p>
              <p className="text-sm text-muted-foreground">{t('settings.likesDescription')}</p>
            </div>
            <Switch
              checked={preferences.postLikes}
              onCheckedChange={(checked) => setPreferences({ ...preferences, postLikes: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{t('settings.mentions')}</p>
              <p className="text-sm text-muted-foreground">{t('settings.mentionsDescription')}</p>
            </div>
            <Switch
              checked={preferences.mentions}
              onCheckedChange={(checked) => setPreferences({ ...preferences, mentions: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{t('settings.newFollowers')}</p>
              <p className="text-sm text-muted-foreground">{t('settings.newFollowersDescription')}</p>
            </div>
            <Switch
              checked={preferences.newFollowers}
              onCheckedChange={(checked) => setPreferences({ ...preferences, newFollowers: checked })}
            />
          </div>
        </div>

        <div className="border-t pt-6 space-y-4">
          <h3 className="text-sm font-medium">{t('settings.achievementNotifications')}</h3>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{t('settings.levelUp')}</p>
              <p className="text-sm text-muted-foreground">{t('settings.levelUpDescription')}</p>
            </div>
            <Switch
              checked={preferences.levelUp}
              onCheckedChange={(checked) => setPreferences({ ...preferences, levelUp: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{t('settings.badgesEarned')}</p>
              <p className="text-sm text-muted-foreground">{t('settings.badgesEarnedDescription')}</p>
            </div>
            <Switch
              checked={preferences.badgeEarned}
              onCheckedChange={(checked) => setPreferences({ ...preferences, badgeEarned: checked })}
            />
          </div>
        </div>

        <div className="border-t pt-6 space-y-4">
          <h3 className="text-sm font-medium">{t('settings.emailNotifications')}</h3>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{t('settings.weeklyDigest')}</p>
              <p className="text-sm text-muted-foreground">{t('settings.weeklyDigestDescription')}</p>
            </div>
            <Switch
              checked={preferences.emailDigest}
              onCheckedChange={(checked) => setPreferences({ ...preferences, emailDigest: checked })}
            />
          </div>
        </div>

        <p className="text-sm text-muted-foreground">{t('settings.notificationNote')}</p>
      </CardContent>
    </Card>
  );
}

function PrivacySettings() {
  const { t } = useTranslation('community');
  const [privacy, setPrivacy] = useState({
    profileVisibility: 'public',
    allowMessages: 'everyone',
    showOnlineStatus: true,
    showActivityStatus: true,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('settings.privacySettings')}</CardTitle>
        <CardDescription>{t('settings.privacySettingsDescription')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div>
            <Label htmlFor="profileVisibility">{t('settings.profileVisibility')}</Label>
            <Select
              value={privacy.profileVisibility}
              onValueChange={(value) => setPrivacy({ ...privacy, profileVisibility: value })}
            >
              <SelectTrigger id="profileVisibility" className="mt-1 w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="public">{t('settings.profileVisibilityPublic')}</SelectItem>
                <SelectItem value="members">{t('settings.profileVisibilityMembers')}</SelectItem>
                <SelectItem value="followers">{t('settings.profileVisibilityFollowers')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="allowMessages">{t('settings.whoCanMessage')}</Label>
            <Select
              value={privacy.allowMessages}
              onValueChange={(value) => setPrivacy({ ...privacy, allowMessages: value })}
            >
              <SelectTrigger id="allowMessages" className="mt-1 w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="everyone">{t('settings.messageEveryone')}</SelectItem>
                <SelectItem value="followers">{t('settings.messageFollowers')}</SelectItem>
                <SelectItem value="nobody">{t('settings.messageNobody')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="border-t pt-6 space-y-4">
          <h3 className="text-sm font-medium">{t('settings.activityStatus')}</h3>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{t('settings.showOnlineStatus')}</p>
              <p className="text-sm text-muted-foreground">{t('settings.showOnlineStatusDescription')}</p>
            </div>
            <Switch
              checked={privacy.showOnlineStatus}
              onCheckedChange={(checked) => setPrivacy({ ...privacy, showOnlineStatus: checked })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{t('settings.showActivityStatus')}</p>
              <p className="text-sm text-muted-foreground">{t('settings.showActivityStatusDescription')}</p>
            </div>
            <Switch
              checked={privacy.showActivityStatus}
              onCheckedChange={(checked) => setPrivacy({ ...privacy, showActivityStatus: checked })}
            />
          </div>
        </div>

        <p className="text-sm text-muted-foreground">{t('settings.privacyNote')}</p>
      </CardContent>
    </Card>
  );
}

function BlockedUsersSettings() {
  const { t } = useTranslation('community');
  const { toast } = useToast();
  const [userToUnblock, setUserToUnblock] = useState<{
    userId: string;
    name: string;
  } | null>(null);

  const { data, isLoading, refetch } = communityTrpc.moderation.getBlockedUsers.useQuery({
    limit: 50,
  });

  const unblockUser = communityTrpc.moderation.unblockUser.useMutation({
    onSuccess: () => {
      toast({
        title: t('settings.unblockSuccess'),
        description: t('settings.unblockSuccessDescription'),
      });
      refetch();
      setUserToUnblock(null);
    },
    onError: (error) => {
      toast({ title: t('common.error'), description: error.message, variant: 'destructive' });
    },
  });

  const handleUnblock = () => {
    if (userToUnblock) {
      unblockUser.mutate({ userId: userToUnblock.userId });
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6 space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4">
              <Skeleton className="h-10 w-10 rounded-full" />
              <Skeleton className="h-4 flex-1" />
              <Skeleton className="h-8 w-20" />
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  const blockedUsers = data?.blockedUsers ?? [];

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{t('settings.blockedUsers')}</CardTitle>
          <CardDescription>{t('settings.blockedUsersDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          {blockedUsers.length === 0 ? (
            <div className="py-8 text-center">
              <UserX className="mx-auto h-12 w-12 text-muted-foreground" aria-hidden="true" />
              <p className="mt-4 text-muted-foreground">{t('settings.noBlockedUsers')}</p>
            </div>
          ) : (
            <div className="divide-y">
              {blockedUsers.map((blocked) => {
                const displayName =
                  blocked.displayName ||
                  [blocked.firstName, blocked.lastName].filter(Boolean).join(' ') ||
                  'Anonymous';
                const initial = displayName.slice(0, 1).toUpperCase();

                return (
                  <div key={blocked.blockId} className="flex items-center gap-4 py-4">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={blocked.profileImageUrl ?? undefined} />
                      <AvatarFallback className="bg-primary/10 text-primary">
                        {initial}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <p className="font-medium">{displayName}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(blocked.blockedAt), { addSuffix: true })}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setUserToUnblock({ userId: blocked.userId, name: displayName })}
                    >
                      {t('settings.unblock')}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!userToUnblock} onOpenChange={() => setUserToUnblock(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('settings.unblockConfirm', { name: userToUnblock?.name })}</DialogTitle>
            <DialogDescription>{t('settings.unblockDescription')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserToUnblock(null)}>
              {t('settings.cancel')}
            </Button>
            <Button onClick={handleUnblock} disabled={unblockUser.isPending}>
              {unblockUser.isPending ? t('settings.unblocking') : t('settings.unblock')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
